import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getBrandLocations } from '../../../api/entity.api';
import SectionHeader from '../../../components/ui/section-header';
import { IEntityLocation } from '../../../types/entity.types';

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export const BrandLocations: React.FC = () => {
  const params = useParams<{ id: string }>();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);

  const { data: entityLocation } = useQuery({
    queryKey: ['getEntityLocation', params.id, debouncedSearch],
    queryFn: () =>
      getBrandLocations(params.id || '', {
        page: 1,
        limit: 100,
        search: debouncedSearch
      }),
    enabled: !!params.id
  });

  return (
    <section className="flex flex-col gap-y-4">
      <SectionHeader
        title={<h1 className="text-lg font-semibold">Locations</h1>}
      />
      <div className="mx-auto w-full max-w-[calc(100%_-_2rem)]">
        <div className="relative mb-6">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value?.trim() || '')}
            placeholder="Search"
            className="w-full rounded-md border-0 bg-gray-100 py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-gray-200"
          />
        </div>
        <div className="divide-y divide-gray-300 px-2">
          {entityLocation?.success &&
            entityLocation?.data?.map((branch: IEntityLocation) => (
              <div key={branch.entity_id} className="py-4 first:pt-0 last:pb-0">
                <h4 className="text-md font-medium">{branch.entity_name}</h4>
                <p className="text-sm text-gray-600">
                  {[
                    branch.entity_address,
                    branch.entity_zipcode,
                    branch.entity_state,
                    branch.entity_country
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              </div>
            ))}
        </div>
      </div>
    </section>
  );
};
